/**
 * Google Ads website tag (AW-) for conversion tracking / promo eligibility.
 * Account conversion ID: AW-18404933566
 */

import {
	ensureAutoTrackingConsent,
	ensureGtagConsentApi,
	hasConsentForAds,
} from "@/lib/analytics/ga4-consent";

declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag?: (...args: unknown[]) => void;
	}
}

export const GOOGLE_ADS_ID =
	process.env.NEXT_PUBLIC_GOOGLE_ADS_ID?.trim() || "AW-18404933566";

/** Event snippet label for "Website purchase (Google tag)" conversion action. */
export const GOOGLE_ADS_PURCHASE_SEND_TO =
	process.env.NEXT_PUBLIC_GOOGLE_ADS_PURCHASE_SEND_TO?.trim() ||
	"AW-18404933566/xkWwCL-304IdEL7_k8hE";

/** Register the Google Ads config alongside GA4 (idempotent). */
export function configGoogleAdsTag() {
	if (typeof window === "undefined" || !GOOGLE_ADS_ID) return;
	ensureGtagConsentApi();
	window.gtag?.("config", GOOGLE_ADS_ID);
}

function adsConversionLabel(): string | null {
	const label = GOOGLE_ADS_PURCHASE_SEND_TO.split("/")[1]?.trim();
	return label || null;
}

function adsConversionId(): string | null {
	const id = GOOGLE_ADS_ID.replace(/^AW-/i, "").trim();
	return id || null;
}

/** Held so the browser does not cancel the conversion image before it is sent. */
const pendingAdsPixels: HTMLImageElement[] = [];

/** Direct GET so the hit is not dropped while gtag still has ad cookies off. */
function sendAdsPurchasePixel(params: {
	currency: string;
	value: number;
	transaction_id: string;
}) {
	const id = adsConversionId();
	const label = adsConversionLabel();
	if (!id || !label) return;

	const key = `aw_purchase_pixel:${params.transaction_id}`;
	try {
		if (sessionStorage.getItem(key)) return;
		sessionStorage.setItem(key, "1");
	} catch {
		/* still send if storage is blocked */
	}

	const url = new URL(`https://www.googleadservices.com/pagead/conversion/${id}/`);
	url.searchParams.set("random", String(Date.now()));
	url.searchParams.set("cv", "11");
	url.searchParams.set("fst", String(Date.now()));
	url.searchParams.set("num", "1");
	url.searchParams.set("value", String(params.value));
	url.searchParams.set("currency_code", params.currency);
	url.searchParams.set("label", label);
	url.searchParams.set("guid", "ON");
	url.searchParams.set("script", "0");
	url.searchParams.set("transaction_id", params.transaction_id);

	const img = new Image();
	pendingAdsPixels.push(img);
	const release = () => {
		const index = pendingAdsPixels.indexOf(img);
		if (index >= 0) pendingAdsPixels.splice(index, 1);
	};
	img.onload = release;
	img.onerror = release;
	img.src = url.toString();
	try {
		void fetch(url.toString(), { mode: "no-cors", keepalive: true });
	} catch {
		/* image hit is the fallback */
	}
}

function whenAdsScriptReady(onReady: () => void) {
	const selector = `script[src*="id=${GOOGLE_ADS_ID}"]`;
	const existing = document.querySelector(selector) as HTMLScriptElement | null;
	if (existing?.dataset.adsLoaded === "1") {
		onReady();
		return;
	}
	if (existing) {
		existing.addEventListener("load", () => {
			existing.dataset.adsLoaded = "1";
			onReady();
		}, { once: true });
		window.setTimeout(onReady, 800);
		return;
	}

	const script = document.createElement("script");
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
	script.addEventListener("load", () => {
		script.dataset.adsLoaded = "1";
		onReady();
	}, { once: true });
	document.head.appendChild(script);
}

function sendAdsPurchaseGtag(params: {
	currency: string;
	value: number;
	transaction_id: string;
}) {
	ensureGtagConsentApi();
	ensureAutoTrackingConsent();
	configGoogleAdsTag();
	if (typeof window.gtag !== "function") return;
	window.gtag("event", "conversion", {
		send_to: GOOGLE_ADS_PURCHASE_SEND_TO,
		value: params.value,
		currency: params.currency,
		transaction_id: params.transaction_id,
	});
}

/**
 * Fire the Ads website purchase conversion (needed for the £250 tracking promo).
 * Ad cookies are turned on first. The hit is sent after the Ads script loads,
 * and also as a direct request so it is not lost in the consent queue.
 */
export function googleAdsPurchaseConversion(params: {
	currency: string;
	value: number;
	transaction_id: string;
}) {
	if (typeof window === "undefined" || !GOOGLE_ADS_PURCHASE_SEND_TO) return;

	ensureAutoTrackingConsent();
	if (!hasConsentForAds()) return;

	sendAdsPurchasePixel(params);
	whenAdsScriptReady(() => sendAdsPurchaseGtag(params));
}
